import { Form } from 'antd';
import { useCallback } from 'react';

import { useAuthenticateByEmailMutation } from '@app/api/Authentication';
import { handleCatchError } from '@app/services';

import { useAuthorizationPreparedForm } from '@pages/Authorization/hooks/useAuthorizationPreparedForm';

interface EmailFormValues {
  captcha: string;
  email: string;
}

interface UseAuthorizationEmailProps {
  email: string;
  isEnabled: boolean;
  setAccessCode: (code: boolean) => void;
  setEmail: (email: string) => void;
}

export const useAuthorizationEmail = ({
  email,
  isEnabled,
  setAccessCode,
  setEmail,
}: UseAuthorizationEmailProps) => {
  const [form] = Form.useForm<EmailFormValues>();

  const {
    captchaChangeHandler,
    captchaImage,
    clearServerErrorHandler,
    emailChangeHandler,
    isPageLoader,
    isSubmitLoader,
    registerAuthAttemptHandler,
    resetCaptchaFieldHandler,
    serverError,
    setServerError,
    setSubmitLoader,
    validateCaptchaHandler,
  } = useAuthorizationPreparedForm({
    email,
    getEmailFieldValue: () => form.getFieldValue('email') || '',
    isEnabled,
    setCaptchaFieldValue: (value) => form.setFieldValue('captcha', value),
    setEmail,
    setEmailFieldValue: (value) => form.setFieldValue('email', value),
  });

  const [authenticateByEmail] = useAuthenticateByEmailMutation();

  const finishHandler = useCallback(
    async (values: EmailFormValues) => {
      const isCaptchaValid = await validateCaptchaHandler();

      if (!isCaptchaValid) {
        return;
      }

      setSubmitLoader(true);

      try {
        await authenticateByEmail({ Email: values.email }).unwrap();
        setAccessCode(true);
        setEmail(values.email);
      } catch (error) {
        setAccessCode(false);
        setServerError(handleCatchError(error));
      } finally {
        registerAuthAttemptHandler();
        resetCaptchaFieldHandler();
        setSubmitLoader(false);
      }
    },
    [
      authenticateByEmail,
      registerAuthAttemptHandler,
      resetCaptchaFieldHandler,
      setAccessCode,
      setEmail,
      setServerError,
      setSubmitLoader,
      validateCaptchaHandler,
    ]
  );

  return {
    captchaChangeHandler,
    captchaImage,
    clearServerErrorHandler,
    emailChangeHandler,
    finishHandler,
    form,
    isPageLoader,
    isSubmitLoader,
    serverError,
  };
};
